import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/primitives";
import { TrendingUp } from "lucide-react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import { getFeelingArrows } from "@/utils/feeling-utils";
import type { ChartData, ChartTooltipData } from "../types";
import { getFirstThreeSentences } from "../utils";

interface MoodChartProps {
	chartData: ChartData[];
	onChartClick: (data: any) => void;
}

export const MoodChart = ({ chartData, onChartClick }: MoodChartProps) => {
	if (chartData.length === 0) return null;

	// Calculate min and max from visible data only
	const feelingValues = chartData.map((d) => d.feeling);
	const rollingAverageValues = chartData
		.map((d) => d.rollingAverage)
		.filter((v) => v !== undefined && v !== null) as number[];

	const allValues = [...feelingValues, ...rollingAverageValues];
	const minValue = Math.min(...allValues);
	const maxValue = Math.max(...allValues);

	// Add padding (10% on each side), with fallback if all values are the same
	const range = maxValue - minValue;
	const padding = range > 0 ? range * 0.1 : Math.abs(minValue) * 0.1 || 1;
	const domainMin = minValue - padding;
	const domainMax = maxValue + padding;

	return (
		<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
						<TrendingUp className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold text-foreground">
							Your Mood Journey
						</CardTitle>
						<CardDescription className="text-sm mt-1">
							Click on any point to jump to that check-in below
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-6">
				<div className="h-48 sm:h-64">
					<ResponsiveContainer width="100%" height="100%">
						<LineChart data={chartData} onClick={onChartClick}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="date" />
							<YAxis
								tick={false}
								domain={[domainMin, domainMax]}
								label={{
									value: "Overall mood trajectory",
									angle: -90,
									position: "insideLeft",
									style: { textAnchor: "middle" },
								}}
							/>
							<Tooltip
								content={({ active, payload, label }) => {
									if (!active || !payload || payload.length === 0) return null;

									const data = payload[0].payload as ChartTooltipData;
									const arrows = getFeelingArrows(data.generalFeeling || 0);
									const description = data.description
										? getFirstThreeSentences(data.description)
										: "No description provided";

									return (
										<div className="rounded-md border bg-popover px-3 py-2 text-xs sm:text-sm text-popover-foreground shadow-md max-w-xs sm:max-w-sm">
											<div className="flex items-center justify-between mb-1">
												<span className="font-medium">{label}</span>
												<span className="flex items-center">{arrows}</span>
											</div>
											<p className="text-muted-foreground leading-snug">
												{description}
											</p>
											{data.reflectionId && (
												<div className="mt-2 text-[11px] sm:text-xs text-foreground">
													Reflection:{" "}
													<a
														className="text-primary underline"
														href={`/reflection/${data.reflectionId}`}
													>
														{data.reflectionTitle || "View reflection"}
													</a>
												</div>
											)}
										</div>
									);
								}}
							/>
							<Line
								type="monotone"
								dataKey="feeling"
								stroke="hsl(var(--primary))"
								strokeWidth={2}
								dot={{
									fill: "hsl(var(--primary))",
									strokeWidth: 2,
									r: 2,
								}}
							/>
							<Line
								type="monotone"
								dataKey="rollingAverage"
								stroke="hsl(var(--success))"
								strokeWidth={2}
								strokeDasharray="5 5"
								dot={false}
							/>
						</LineChart>
					</ResponsiveContainer>
				</div>
			</CardContent>
		</Card>
	);
};
